import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, fmtDate, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import {
  IB_BUG_STATUSES, IB_BUG_TERMINAL, IB_IDEA_STATUSES, IB_IDEA_TERMINAL,
  IB_SEVERITIES, IB_PRIORITIES, IB_MODULE_AREAS, IB_STATUS_COLORS, IB_SEVERITY_COLORS, IB_PRIORITY_COLORS,
} from "../lib/fields";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CommentsAudit } from "../components/CommentsAudit";
import { toast } from "sonner";
import { ArrowLeft, Bug, Lightbulb, Sparkles, ChevronUp, CheckCircle2, Upload, Paperclip, Map } from "lucide-react";

const TYPE_META = {
  Bug: { icon: Bug, color: "text-red-600", bg: "bg-red-50 text-red-700" },
  Idea: { icon: Lightbulb, color: "text-amber-500", bg: "bg-amber-50 text-amber-700" },
  "Feature Request": { icon: Sparkles, color: "text-blue-600", bg: "bg-blue-50 text-blue-700" },
};

export default function IdeaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, isManager, canWrite } = useApp();
  const [item, setItem] = useState(null);
  const [users, setUsers] = useState([]);
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);

  const load = () => api.get(`/ideas/${id}`).then((r) => setItem(r.data)).catch(() => {});
  const loadFiles = () => api.get(`/files/ideas/${id}`).then((r) => setFiles(r.data)).catch(() => {});
  useEffect(() => { load(); loadFiles(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, [id]);

  if (!item) return <p className="text-gray-400">{t("loading")}</p>;

  const meta = TYPE_META[item.type] || TYPE_META.Bug;
  const Icon = meta.icon;
  const isBug = item.type === "Bug";
  const mainStatuses = isBug ? IB_BUG_STATUSES : IB_IDEA_STATUSES;
  const terminal = isBug ? IB_BUG_TERMINAL : IB_IDEA_TERMINAL;
  const allStatuses = [...mainStatuses, ...terminal];
  const statusIdx = mainStatuses.indexOf(item.status);
  const assignees = users.filter((u) => ["admin", "sales_manager"].includes(u.role));

  const patch = async (updates) => {
    try {
      const r = await api.put(`/ideas/${id}`, { ...item, ...updates });
      setItem(r.data);
      toast.success(t("save") + " ✓");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const vote = async () => {    try {
      const r = await api.post(`/ideas/${id}/vote`);
      setItem((p) => ({ ...p, votes: r.data.votes, voted: r.data.voted }));
    } catch (e) { toast.error(errDetail(e)); }
  };

  const assign = (userId) => {
    const u = users.find((x) => x.id === userId);
    patch({ assigned_to_id: userId === "__none__" ? null : userId, assigned_to: userId === "__none__" ? null : u?.name });
  };

  const toRoadmap = async () => {
    try {
      const r = await api.post(`/ideas/${id}/to-roadmap`);
      if (r.data.already) toast.info(`${t("ib_already_on_roadmap")}: ${r.data.item_number}`);
      else toast.success(`${t("ib_moved_to_roadmap")}: ${r.data.item_number}`);
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const uploadFile = async (file) => {
    if (files.length >= 5) { toast.error(t("ib_max_files")); return; }
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/files/ideas/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); loadFiles(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className="space-y-5" data-testid="idea-detail-page">
      <button onClick={() => navigate("/ideas")} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1 transition-colors duration-150" data-testid="back-to-ideas">
        <ArrowLeft className="h-4 w-4" />{t("ideas_bugs")}
      </button>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            {!isBug && (
              <button onClick={vote} data-testid="idea-vote-btn"
                className={`flex flex-col items-center rounded-xl border px-4 py-2 transition-colors duration-150 ${
                  item.voted ? "border-[#F5A623] bg-amber-50 text-[#1B2A4A]" : "border-gray-200 text-gray-500 hover:border-[#F5A623]"}`}>
                <ChevronUp className={`h-5 w-5 ${item.voted ? "text-[#F5A623]" : ""}`} strokeWidth={2.5} />
                <span className="text-lg font-bold font-mono" data-testid="idea-vote-count">{item.votes || 0}</span>
                <span className="text-[10px] uppercase tracking-wide">{t("ib_votes")}</span>
              </button>
            )}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono font-medium text-gray-400 text-sm" data-testid="idea-number">{item.ib_number}</span>
                <Badge className={`${meta.bg} hover:bg-inherit`}><Icon className={`h-3 w-3 mr-1 ${meta.color}`} />{t(`ib_type_${item.type.replace(/\s+/g, "_").toLowerCase()}`)}</Badge>
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-[#1B2A4A]" style={{ fontFamily: "Work Sans" }} data-testid="idea-title">{item.title}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-gray-500">
                <span>{item.module_area}</span>·
                <span>{t("ib_submitted_by")}: {item.submitted_by}</span>·
                <span>{fmtDate(item.submitted_at)}</span>
                {isBug && <>·<Badge className={`${IB_SEVERITY_COLORS[item.severity] || ""} hover:bg-inherit`}>{item.severity}</Badge></>}
                {item.priority && <>·<Badge className={`${IB_PRIORITY_COLORS[item.priority] || ""} hover:bg-inherit`}>{item.priority}</Badge></>}
              </div>
            </div>
          </div>
          <Badge className={`${IB_STATUS_COLORS[item.status] || ""} hover:bg-inherit text-sm`} data-testid="idea-status-badge">{item.status}</Badge>
        </div>

        {/* Status workflow */}
        <div className="mt-6">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1.5" data-testid="idea-status-workflow">
              {mainStatuses.map((s, i) => {
                const isCurrent = item.status === s;
                const done = statusIdx >= 0 && i < statusIdx;
                return (
                  <button key={s} disabled={!isManager}
                    data-testid={`idea-status-step-${i}`}
                    onClick={() => isManager && patch({ status: s })}
                    className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors duration-150 flex items-center gap-1 ${
                      isCurrent ? "bg-[#1B2A4A] text-white border-[#1B2A4A] font-semibold"
                        : done ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"} ${!isManager ? "cursor-default" : ""}`}>
                    {done && <CheckCircle2 className="h-3 w-3" />}{s}
                  </button>
                );
              })}
            </div>
            {isManager && (
              <Select value={allStatuses.includes(item.status) ? item.status : ""} onValueChange={(v) => patch({ status: v })}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="idea-status-jump">
                  <SelectValue placeholder={t("ib_jump_status")} />
                </SelectTrigger>
                <SelectContent>
                  {allStatuses.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">{t("ib_description")}</h2>
            <p className="text-sm text-gray-700 whitespace-pre-wrap" data-testid="idea-description">{item.description}</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" />{t("ib_attachments")} ({files.length}/5)</h3>
              {canWrite && files.length < 5 && (<>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
                  onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} />
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => fileRef.current?.click()} data-testid="idea-upload-btn">
                  <Upload className="h-3 w-3 mr-1" />{t("upload")}
                </Button>
              </>)}
            </div>
            {files.length === 0 && <p className="text-xs text-gray-400">–</p>}
            <div className="grid grid-cols-3 gap-2" data-testid="idea-files">
              {files.map((f) => {
                const url = `${API}/files/download/${f.id}?auth=${localStorage.getItem("lf_token")}`;
                return f.content_type?.startsWith("image/") ? (
                  <a key={f.id} href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt={f.original_filename} className="rounded-md border border-gray-200 object-cover h-20 w-full" />
                  </a>
                ) : (
                  <a key={f.id} href={url} target="_blank" rel="noreferrer"
                    className="text-[11px] text-blue-600 underline flex items-center justify-center text-center h-20 border rounded-md px-1 break-all">
                    {f.original_filename}
                  </a>
                );
              })}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <CommentsAudit module="ideas" id={id} />
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">{t("ib_details")}</h3>
            {!isManager && <p className="text-xs text-gray-400">{t("ib_manager_only")}</p>}

            {!isBug && (
              <div className="space-y-1.5" data-testid="idea-roadmap-block">
                {item.roadmap_item_id ? (
                  <button onClick={() => navigate(`/roadmap/${item.roadmap_item_id}`)}
                    data-testid="idea-open-roadmap-btn"
                    className="w-full flex items-center justify-center gap-1.5 text-xs font-medium rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 px-3 py-2 hover:bg-emerald-100 transition-colors duration-150">
                    <Map className="h-3.5 w-3.5" />{t("ib_on_roadmap")}: {item.roadmap_item_number}
                  </button>
                ) : isManager ? (
                  <Button size="sm" className="w-full bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold"
                    data-testid="idea-to-roadmap-btn" onClick={toRoadmap}>
                    <Map className="h-3.5 w-3.5 mr-1.5" />{t("ib_move_to_roadmap")}
                  </Button>
                ) : null}
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">{t("ib_priority")}</Label>
              <Select value={item.priority || "__none__"} onValueChange={(v) => isManager && patch({ priority: v === "__none__" ? null : v })} disabled={!isManager}>
                <SelectTrigger className="h-9" data-testid="idea-priority-select"><SelectValue placeholder="–" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">–</SelectItem>
                  {IB_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">{t("ib_assigned_to")}</Label>
              <Select value={item.assigned_to_id || "__none__"} onValueChange={assign} disabled={!isManager}>
                <SelectTrigger className="h-9" data-testid="idea-assign-select"><SelectValue placeholder="–" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">–</SelectItem>
                  {assignees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {isBug && (
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">{t("severity")}</Label>
                <Select value={item.severity || "Minor"} onValueChange={(v) => isManager && patch({ severity: v })} disabled={!isManager}>
                  <SelectTrigger className="h-9" data-testid="idea-severity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{IB_SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">{t("ib_module_area")}</Label>
              <Select value={item.module_area || "Other"} onValueChange={(v) => isManager && patch({ module_area: v })} disabled={!isManager}>
                <SelectTrigger className="h-9" data-testid="idea-module-select"><SelectValue /></SelectTrigger>
                <SelectContent>{IB_MODULE_AREAS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">{t("ib_linked_release")}</Label>
              <Input className="h-9" disabled={!isManager} defaultValue={item.linked_release || ""}
                data-testid="idea-release-input" placeholder="v2.4"
                onBlur={(e) => isManager && e.target.value !== (item.linked_release || "") && patch({ linked_release: e.target.value })} />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">{t("ib_duplicate_of")}</Label>
              <Input className="h-9 font-mono" disabled={!isManager} defaultValue={item.duplicate_of || ""}
                data-testid="idea-duplicate-input" placeholder="IB-2026-0001"
                onBlur={(e) => isManager && e.target.value !== (item.duplicate_of || "") && patch({ duplicate_of: e.target.value })} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
